
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TeamFormData, TeamFormSchema, TeamDetail } from '../../types/settings/TeamTypes';
import { createTeam, updateTeam } from '@/actions/data-fetch-teams';
import { getUsers } from '@/actions/data-fetch-users';
import { toastMessage } from '@/utils/toast-message';
import {
  Building2,
  FileText,
  Crown,
  Loader2,
  AlertCircle,
  Save
} from 'lucide-react';

interface TeamFormProps {
  onClose?: () => void;
  uuid?: string;
  initialData?: TeamDetail | null;
  onSuccess: (team: TeamDetail) => void;
}

const TeamForm: React.FC<TeamFormProps> = ({
  onClose,
  uuid,
  initialData,
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
  } = useForm<TeamFormData>({
    resolver: zodResolver(TeamFormSchema),
    defaultValues: {
      team_name: '',
      team_description: '',
      team_lead_uuid: '',
    },
  });

  // Fetch available users for team lead selection
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getUsers({ limit: 100 });
      
      if (response.success || response.status === 'success') {
        const formattedUsers = response.data?.users?.map((user: any) => ({
          uuid: user.uuid,
          userName: user.userName,
          userEmail: user.userEmail,
          role: user.role,
        })) || [];
        setAvailableUsers(formattedUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toastMessage('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };


  // Initialize form data
  useEffect(() => {
    fetchUsers();
    
    if (initialData) {
      setValue('team_name', initialData.team_name || '');
      setValue('team_description', initialData.team_description || '');
      setValue('team_lead_uuid', initialData.team_lead_uuid || '');
    }
  }, [initialData, setValue]);

  const onSubmit = async (data: TeamFormData) => {
    setIsSubmitting(true);
    
    try {
      const apiData = {
        team_name: data.team_name,
        team_description: data.team_description || undefined,
        team_lead_uuid: data.team_lead_uuid || undefined,
      };

      let response;
      if (uuid) {
        response = await updateTeam(uuid, apiData);
      } else {
        response = await createTeam(apiData);
      }
      
      if (!response.success && response.status !== 'success') {
        throw new Error(response.message || 'Failed to save team');
      }
      
      const savedTeam = response.data?.data || response.data;
      onSuccess(savedTeam);
      toastMessage(
        uuid ? 'Team updated successfully' : 'Team created successfully',
        'success'
      );
    } catch (error: any) {
      toastMessage(error.message || 'Failed to save team', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputField = ({ 
    label, 
    icon: Icon, 
    error, 
    description,
    children 
  }: { 
    label: string; 
    icon: any; 
    error?: string; 
    description?: string;
    children: React.ReactNode;
  }) => (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
        <Icon className="w-4 h-4 mr-2 text-gray-400" />
        {label}
      </label>
      {children}
      {error && (
        <div className="flex items-center text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
      {description && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex items-center space-x-3">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {uuid ? 'Updating team...' : 'Creating team...'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Please wait while we save your changes
              </p>
            </div>
          </div>
        </div>
      )}
      

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col">
        <div className={`flex-1 overflow-y-auto p-6 transition-opacity ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="space-y-6 max-w-2xl">

            {/* Team Name */}
            <InputField
              label="Team Name"
              icon={Building2}
              error={errors.team_name?.message}
              description="A clear, descriptive name for this team"
            >
              <input
                {...register('team_name')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Sales Team Alpha, Marketing Squad"
              />
            </InputField>

            {/* Team Description */}
            <InputField
              label="Team Description"
              icon={FileText}
              error={errors.team_description?.message}
              description="Describe the team's purpose and responsibilities"
            >
              <textarea
                {...register('team_description')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Describe what this team does and their key responsibilities..."
              />
            </InputField>


            {/* Team Lead */}
            <InputField
              label="Team Lead"
              icon={Crown}
              error={errors.team_lead_uuid?.message}
              description="Who will lead this team?"
            >
              <select
                {...register('team_lead_uuid')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                disabled={loadingUsers}
              >
                <option value="">Select Team Lead (Optional)</option>
                {availableUsers.map((user) => (
                  <option key={user.uuid} value={user.uuid}>
                    {user.userName} ({user.userEmail})
                  </option>
                ))}
              </select>
              {loadingUsers && (
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Loading users...
                </div>
              )}
            </InputField>

          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-end space-x-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Cancel
              </button>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brandColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isSubmitting 
                ? (uuid ? 'Updating...' : 'Creating...')
                : (uuid ? 'Update Team' : 'Create Team')
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TeamForm;